import { apiClient } from './client';
import type {
  KpiData, Task, FinanceSummary, FinancePL, FinanceCashflow, FinanceBudgetBreakdown,
  ReportTypeMeta, GenerateReportRequest, GenerateReportResponse,
  LoteWithBoundary, GeoJSONPolygon,
  NotificationsResponse,
  ChatRequest, ChatResponse, WebChatSession, WebChatSessionSummary,
  NdviResult, NdviHistoryResponse,
  TaskAudience, AudiencePreviewResponse,
} from '../types/api.types';

export const fetchKpis = async (ranchoId?: string | null): Promise<KpiData> =>
  (await apiClient.get('/api/dashboard/kpis', { params: ranchoId ? { ranchoId } : undefined })).data;

export const fetchTasks = async (date?: string, ranchoId?: string | null): Promise<Task[]> =>
  (await apiClient.get('/api/tasks', { params: { date, ...(ranchoId ? { ranchoId } : {}) } })).data;

export const updateTaskStatus = async (taskId: string, loteId: string, status: string) =>
  (await apiClient.put(`/api/tasks/${taskId}/status`, { loteId, status })).data;

export interface CreateTaskInput {
  /** Omitir para tarea general (requiere ranchoId). */
  loteId?:       string;
  /** Zona del lote donde se hace la tarea. Omitir = todo el lote. */
  moduleId?:     string;
  ranchoId?:     string;
  type:          string;
  title:         string;
  description?:  string;
  scheduledDate: string;
  dueDate?:      string;
  priority?:     'HIGH' | 'MEDIUM' | 'LOW';
  assignedTo?:   string;
  /** Instante ISO en que debe salir el aviso de Telegram. Omitir = avisar ahora. */
  notifyAt?:     string;
  batchId?:      string;
  /** Audiencia + lista final de destinatarios (phoneNumbers) ya editada en la vista previa. */
  audience?:     TaskAudience;
  recipients?:   string[];
}

export const createTask = async (input: CreateTaskInput): Promise<Task> =>
  (await apiClient.post('/api/tasks', input)).data;

export interface EditTaskInput {
  loteId:        string;
  title?:        string;
  description?:  string;
  scheduledDate?: string;
  dueDate?:      string;
  priority?:     'HIGH' | 'MEDIUM' | 'LOW';
  assignedTo?:   string;
  notes?:        string;
  audience?:     TaskAudience;
  recipients?:   string[];
  /** Hora local del rancho (sin zona) o null para cancelar el aviso programado. */
  notifyAt?:     string | null;
}

export const updateTask = async (taskId: string, input: EditTaskInput): Promise<void> =>
  (await apiClient.put(`/api/tasks/${taskId}`, input)).data;

export const deleteTask = async (taskId: string, loteId: string): Promise<void> =>
  (await apiClient.delete(`/api/tasks/${taskId}`, { params: { loteId } })).data;

export const previewTaskAudience = async (input: { audience: TaskAudience; loteId?: string; ranchoId?: string }): Promise<AudiencePreviewResponse> =>
  (await apiClient.post('/api/tasks/audience-preview', input)).data;

export const recomputeTaskRecipients = async (taskId: string, loteId: string): Promise<{ taskId: string; added: number; total: number }> =>
  (await apiClient.post(`/api/tasks/${taskId}/recompute-recipients`, { loteId })).data;

export interface AssigneeOption {
  kind:  'USER' | 'GROUP';
  id:    string;
  label: string;
  sub?:  string;
}

export const fetchAssignees = async (): Promise<AssigneeOption[]> =>
  (await apiClient.get('/api/assignees')).data;

export const fetchFinanceSummary = async (ranchoId?: string | null): Promise<FinanceSummary> =>
  (await apiClient.get('/api/finance/summary', { params: ranchoId ? { ranchoId } : undefined })).data;

import type { Protocol, LoteBudget } from '../types/api.types';

export const fetchProtocolsSummary = async (): Promise<Protocol[]> =>
  (await apiClient.get('/api/protocols')).data;

export const fetchLoteBudget = async (loteId: string): Promise<LoteBudget[]> =>
  ((await apiClient.get(`/api/finance/lote/${loteId}/budget`)).data.budgets ?? []);

// ── Finance avanzado (ADR-016) ───────────────────────────────────────────────

/** Selector de Rancho: quita `ranchoId` null/undefined para no mandar `?ranchoId=`. */
const withRancho = <T extends { ranchoId?: string | null }>(params: T): Omit<T, 'ranchoId'> & { ranchoId?: string } => {
  const { ranchoId, ...rest } = params;
  return ranchoId ? { ...rest, ranchoId } : rest;
};

export const fetchFinancePL = async (params: { from?: string; to?: string; ranchoId?: string | null } = {}): Promise<FinancePL> =>
  (await apiClient.get('/api/finance/pl', { params: withRancho(params) })).data;

export const fetchCashflow = async (params: { from?: string; to?: string; granularity?: 'week' | 'month'; ranchoId?: string | null } = {}): Promise<FinanceCashflow> =>
  (await apiClient.get('/api/finance/cashflow', { params: withRancho(params) })).data;

export const fetchBudgetBreakdown = async (loteId?: string, ranchoId?: string | null): Promise<FinanceBudgetBreakdown> =>
  (await apiClient.get('/api/finance/budget-breakdown', {
    params: { ...(loteId ? { loteId } : {}), ...(ranchoId ? { ranchoId } : {}) },
  })).data;

export interface CreateBudgetInput {
  ranchoId:       string;
  loteId:         string;
  ciclo:          string;
  totalBudget:    number;
  estimatedYield: number;
  estimatedPrice: number;
  committedAmount?: number;
  /** Moneda distinta de la base de la empresa; omitir para la base. */
  currency?:      string;
  notes?:         string;
}

export const createFinanceBudget = async (input: CreateBudgetInput): Promise<unknown> =>
  (await apiClient.post('/api/finance/budgets', input)).data;

export const closeFinanceBudget = async (budgetId: string, loteId: string): Promise<void> =>
  (await apiClient.delete(`/api/finance/budgets/${budgetId}`, { params: { loteId } })).data;

export const updateCommittedAmount = async (budgetId: string, loteId: string, committedAmount: number): Promise<LoteBudget> =>
  (await apiClient.patch(`/api/finance/budgets/${budgetId}/committed`, { loteId, committedAmount })).data;

// ── PDF Reports (ADR-016 Task 3) ─────────────────────────────────────────────

export const fetchReportTypes = async (): Promise<ReportTypeMeta[]> =>
  (await apiClient.get('/api/reports/types')).data.types;

export const generateReport = async (req: GenerateReportRequest): Promise<GenerateReportResponse> =>
  (await apiClient.post('/api/reports/generate', req)).data;

// ── GIS / Map (ADR-016 Task 4) ─────────────────────────────────────────────

/** `date` (YYYY-MM-DD): cultivo y ciclo vigentes ese día en vez del espejo actual. */
export const fetchLotesForMap = async (ranchoId?: string | null, date?: string): Promise<LoteWithBoundary[]> => {
  const params = { ...(ranchoId ? { ranchoId } : {}), ...(date ? { date } : {}) };
  const res = await apiClient.get('/api/lotes', { params: Object.keys(params).length ? params : undefined });
  return Array.isArray(res.data) ? res.data : (res.data.lotes ?? []);
};

export const patchLoteBoundary = async (
  loteId: string,
  boundary?: GeoJSONPolygon,
  location?: { latitude: number; longitude: number },
): Promise<void> => {
  await apiClient.patch(`/api/lotes/${loteId}/boundary`, { boundary, ...location });
};

// ── Notifications (ADR-016 Task 5) ─────────────────────────────────────────
export const fetchNotifications = async (since?: string): Promise<NotificationsResponse> => {
  const params = since ? { since } : {};
  return (await apiClient.get('/api/notifications', { params })).data;
};

// ── Chat IA (ADR-016 Task 6) ────────────────────────────────────────────────
export const sendChatMessage = async (req: ChatRequest): Promise<ChatResponse> =>
  (await apiClient.post('/api/chat', req)).data;

// ── Chat — historial de sesiones (sidebar) ───────────────────────────────────
export const listChatSessions = async (): Promise<WebChatSessionSummary[]> =>
  (await apiClient.get('/api/chat/sessions')).data.sessions;

export const getChatSession = async (sessionId: string): Promise<WebChatSession> =>
  (await apiClient.get(`/api/chat/sessions/${sessionId}`)).data;

export const renameChatSession = async (sessionId: string, title: string): Promise<void> => {
  await apiClient.patch(`/api/chat/sessions/${sessionId}`, { title });
};

export const deleteChatSession = async (sessionId: string): Promise<void> => {
  await apiClient.delete(`/api/chat/sessions/${sessionId}`);
};

/** `value: null` quita el voto. `messageIndex` es la posición en `messages` de la sesión. */
export const setMessageFeedback = async (
  sessionId: string,
  messageIndex: number,
  value: 'UP' | 'DOWN' | null,
): Promise<void> => {
  await apiClient.put(`/api/chat/sessions/${sessionId}/messages/${messageIndex}/feedback`, { value });
};

// ── NDVI / Agromonitoring (ADR-016 Task 7) ──────────────────────────────────
export const fetchNdvi = async (loteId: string, date?: string): Promise<NdviResult> => {
  const params = date ? { date } : {};
  return (await apiClient.get(`/api/lotes/${loteId}/ndvi`, { params })).data;
};

export const fetchNdviHistory = async (
  loteId: string,
  from?: string,
  to?: string,
): Promise<NdviHistoryResponse> => {
  const params: Record<string, string> = {};
  if (from) params.from = from;
  if (to)   params.to   = to;
  return (await apiClient.get(`/api/lotes/${loteId}/ndvi/history`, { params })).data;
};
